import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, FileCheck, AlertTriangle, Zap, FileText, CheckCircle2, ChevronRight } from 'lucide-react';
import { assessmentApi, queryKeys } from '../services/api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

const PILLAR_INFO = [
  { key: 'pillarI',   label: 'I. Tuân thủ pháp lý', max: 25, color: 'emerald' },
  { key: 'pillarII',  label: 'II. Chất lượng dữ liệu', max: 30, color: 'blue' },
  { key: 'pillarIII', label: 'III. An ninh kết nối', max: 35, color: 'amber' },
  { key: 'pillarIV',  label: 'IV. Tổ chức thực thi', max: 10, color: 'purple' },
];

export default function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: session, isLoading } = useQuery({
    queryKey: queryKeys.sessions.detail(id!),
    queryFn:  () => assessmentApi.get(id!),
    enabled:  !!id,
  });

  const runDQCheck = useMutation({
    mutationFn: () => assessmentApi.runDataQualityCheck(id!),
    onSuccess:  () => toast.success('Đã khởi động kiểm tra chất lượng dữ liệu', { style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' } }),
    onError:    () => toast.error('Lỗi khởi động kiểm tra', { style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' } }),
  });

  const runLegalCheck = useMutation({
    mutationFn: () => assessmentApi.runLegalCheck(id!),
    onSuccess:  () => toast.success('Đã khởi động kiểm tra pháp lý', { style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' } }),
    onError:    () => toast.error('Lỗi khởi động kiểm tra', { style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' } }),
  });

  if (isLoading) return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-slate-500 dark:text-slate-400">
      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
      <p className="text-sm font-black tracking-tight">Đang tải hồ sơ...</p>
    </div>
  );
  if (!session) return <div className="p-12 text-center text-rose-500 font-black bg-rose-500/10 rounded-2xl border border-rose-500/20 tracking-tight leading-loose shadow-sm">Không tìm thấy hồ sơ kiểm toán</div>;

  const breakdown = session.scoreBreakdown || {
    pillarI: session.totalScore ? 19.2 : 0, 
    pillarII: session.totalScore ? 21.8 : 0, 
    pillarIII: session.totalScore ? 24.6 : 0, 
    pillarIV: session.totalScore ? 6.8 : 0
  }; 

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div>
        <Link to="/assessments" className="inline-flex items-center gap-1.5 text-xs font-black text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 transition-colors mb-4 tracking-tight">
          <ArrowLeft size={14} /> Danh sách hồ sơ
        </Link>
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-4 flex-wrap">
              <h2 className="text-3xl font-heading text-slate-900 dark:text-white tracking-tight leading-tight">{session.sessionCode}</h2>
              {session.ranking && (
                <span className={`px-4 py-1 rounded-xl border text-sm font-semibold tracking-tight shadow-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30`}>
                  Loại {session.ranking}
                </span>
              )}
            </div>
            <p className="text-slate-600 dark:text-slate-400 mt-2 font-medium tracking-tight text-sm flex items-center gap-3">
              {session.organization?.name} <span className="text-slate-200 dark:text-white/10 mx-1">|</span> {session.period?.name}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {session.status === 'SUBMITTED' && (
              <>
                <button 
                  onClick={() => runLegalCheck.mutate()} 
                  disabled={runLegalCheck.isPending}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-medium tracking-tight hover:bg-emerald-500/5 transition-all shadow-sm active:scale-95"
                >
                  <FileCheck size={14}/> KT pháp lý
                </button>
                <button 
                  onClick={() => runDQCheck.mutate()} 
                  disabled={runDQCheck.isPending}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cyan-500/30 text-cyan-600 dark:text-cyan-400 text-[10px] font-medium tracking-tight hover:bg-cyan-500/5 transition-all shadow-sm active:scale-95"
                >
                  <Zap size={14}/> KT chất lượng
                </button>
              </>
            )}
            <Link to={`/assessments/${id}/checklist`} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-[10px] font-medium tracking-tight shadow-[0_10px_30px_rgba(99,102,241,0.3)] hover:shadow-[0_15px_40px_rgba(99,102,241,0.5)] transition-all transform hover:-translate-y-0.5 active:scale-95">
              <FileCheck size={16}/> Danh sách tiêu chí
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8">
        {/* Info Sidebar */}
        <div className="col-span-12 lg:col-span-4 space-y-8">
          <div className="glass-panel p-6 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
            <h3 className="text-[10px] text-indigo-600 dark:text-indigo-400 tracking-tight mb-6 font-heading">Chi tiết định danh</h3>
            <dl className="space-y-4">
              {[
                ['Mã hồ sơ', session.sessionCode],
                ['Trạng thái', session.status],
                ['Đơn vị thực hiện', session.organization?.name],
                ['Kỳ đánh giá', session.period?.name],
                ['Thời điểm tạo', dayjs(session.createdAt).format('DD/MM/YYYY HH:mm')],
                ['Cập nhật cuối', dayjs(session.updatedAt).format('DD/MM/YYYY HH:mm')],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between items-start py-2.5 border-b border-slate-100 dark:border-white/5 last:border-0 last:pb-0 group">
                  <dt className="text-[10px] font-semibold text-slate-500 dark:text-slate-500 tracking-tight">{k}</dt>
                  <dd className="text-xs font-semibold text-slate-900 dark:text-white text-right max-w-[180px] break-words tracking-tight">{v ?? '—'}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Quick actions panel */}
          <div className="glass-panel p-6 relative overflow-hidden group border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
            <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-500/20 transition-all" />
            <h3 className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 tracking-tight mb-5 relative z-10 font-heading">Tiện ích truy cập</h3>
            <div className="space-y-3 relative z-10">
              <Link 
                to={`/assessments/${id}/remediations`}
                className="flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 border border-slate-200 dark:border-white/5 transition-all text-xs font-semibold text-slate-700 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white shadow-sm hover:shadow-md"
              >
                <div className="p-2 bg-amber-500/10 rounded-lg text-amber-600 dark:text-amber-500 shadow-inner">
                  <AlertTriangle size={16} /> 
                </div>
                Kiến nghị khắc phục
                <ChevronRight size={14} className="ml-auto text-slate-400 dark:text-slate-600" />
              </Link>
              <button 
                className="flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 border border-slate-200 dark:border-white/5 transition-all text-xs font-medium text-slate-800 dark:text-slate-400 hover:text-slate-950 dark:hover:text-white w-full text-left shadow-sm hover:shadow-md"
              >
                <div className="p-2 bg-indigo-500/10 rounded-lg text-indigo-600 dark:text-indigo-400 shadow-inner">
                  <FileText size={16} />
                </div>
                Xuất báo cáo PDF
                <ChevronRight size={14} className="ml-auto text-slate-400 dark:text-slate-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="col-span-12 lg:col-span-8 space-y-8">
          {session.totalScore !== null && (
            <div className="glass-panel p-8 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm overflow-hidden relative">
               <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/[0.03] blur-[100px] rounded-full" />
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 relative z-10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-inner">
                    <CheckCircle2 size={24} />
                  </div>
                  <div>
                    <h3 className="text-[11px] text-indigo-600 dark:text-indigo-400 tracking-tight font-heading">Tổng hợp kết quả</h3>
                    <p className="text-xl font-medium text-slate-900 dark:text-white tracking-tight mt-0.5 font-heading">Hồ sơ thẩm định dữ liệu</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-6xl font-medium text-slate-900 dark:text-white drop-shadow-[0_10px_10px_rgba(0,0,0,0.05)] dark:drop-shadow-[0_0_15px_rgba(255,255,255,0.1)] tracking-tighter leading-none">
                    {session.totalScore}
                  </p>
                  <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold tracking-tight mt-2">tổng thể / 100</p>
                </div>
              </div>
              
              <div className="space-y-6 bg-slate-50 dark:bg-black/20 p-6 md:p-8 rounded-[2rem] border border-slate-100 dark:border-white/5 shadow-inner relative z-10">
                {PILLAR_INFO.map((p, idx) => {
                  const score = (breakdown as any)?.[p.key] ?? 0;
                  const pct = (score / p.max) * 100;
                  return (
                    <div key={p.key} className="group">
                      <div className="flex justify-between items-center mb-2.5">
                        <span className="text-[11px] font-medium text-slate-600 dark:text-slate-400 group-hover:text-slate-950 dark:group-hover:text-white transition-colors tracking-tight">{p.label}</span>
                        <span className={`text-xs font-medium text-${p.color}-600 dark:text-${p.color}-400 drop-shadow-[0_0_10px_currentColor] tracking-tight`}>
                          {score} <span className="text-[10px] text-slate-400 dark:text-slate-600 font-light">/ {p.max}</span>
                        </span>
                      </div>
                      <div className="h-3 bg-white dark:bg-white/5 rounded-full overflow-hidden shadow-inner flex border border-slate-200/50 dark:border-transparent">
                        <div 
                          className={`h-full rounded-full transition-all duration-1000 ease-out bg-${p.color}-500 shadow-[0_5px_15px_currentColor] opacity-80`}
                          style={{ width: `${pct}%`, transitionDelay: `${idx * 150}ms` }} 
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Checklist stats preview */}
          <div className="glass-panel p-8 relative overflow-hidden border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] shadow-sm">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/[0.02] blur-[110px] rounded-full pointer-events-none" />
            <div className="flex items-center justify-between mb-8 relative z-10">
              <h3 className="text-[11px] text-slate-400 dark:text-slate-500 tracking-tight font-heading">Thống kê 22 tiêu chí</h3>
              <Link to={`/assessments/${id}/checklist`} className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1.5 transition-all tracking-tight group">
                Chi tiết đánh giá <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
              {[
                { label: 'Đạt', count: 0, color: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-500/20', bg: 'bg-emerald-500/5', shadow: 'shadow-sm' },
                { label: 'Một phần', count: 0, color: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/20', bg: 'bg-amber-500/5', shadow: 'shadow-sm' },
                { label: 'Không đạt', count: 0, color: 'text-rose-600 dark:text-rose-400', border: 'border-rose-500/20', bg: 'bg-rose-500/5', shadow: 'shadow-sm' },
                { label: 'Chưa KT', count: 22, color: 'text-slate-600 dark:text-slate-400', border: 'border-slate-200 dark:border-white/10', bg: 'bg-slate-50 dark:bg-white/5', shadow: '' },
              ].map(s => (
                <div key={s.label} className={`rounded-[1.5rem] p-6 border ${s.border} ${s.bg} ${s.shadow} backdrop-blur-md flex flex-col items-center justify-center hover:-translate-y-1 transition-all group shadow-inner`}>
                  <p className={`text-4xl font-medium ${s.color} drop-shadow-[0_5px_10px_rgba(0,0,0,0.05)] dark:drop-shadow-[0_0_10px_currentColor] tracking-tighter`}>{s.count}</p>
                  <p className="text-[9px] font-medium text-slate-500 dark:text-slate-500 mt-2 tracking-tight text-center">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
